import { ApiError } from '@limuzin/kit';

/**
 * Ratings that have not reached the server yet (ADR 0030) — the twin of
 * `RatingOutbox` in the rider app.
 *
 * A rating is a record: the ride was what it was whether the server has heard or
 * not, so a passenger with no signal at «Готово» does not lose their stars. It carries
 * when it was left, and the server writes it then; sent twice, the second is answered
 * `already_rated`, which is taken here as delivered; there is one per ride, so there
 * is no order to keep; and the summary closes at once, saying the stars are kept.
 *
 * A rating the server refuses later is let go of without a word, as the driver's are
 * (ADR 0029). Kept in `localStorage`, so a reload before the signal returns does not
 * lose it either.
 */

export interface HeldRating {
  rideId: string;
  /** Whose rating it is: one passenger's is never sent in another's name. */
  passengerId: string;
  stars: number;
  comment: string;
  /** When it was left, as ISO-8601. */
  at: string;
}

export type RatingLeft =
  | { kind: 'sent' }
  | { kind: 'kept' }
  | { kind: 'refused'; message: string };

/** What sends one rating: `rateRide`, in the app. */
export type RatingDelivery = (rating: HeldRating) => Promise<unknown>;

const KEY = 'limuzin.customer.held-ratings';

/** How long a rating that could not be sent is worth keeping. */
const KEEP_FOR = 7 * 24 * 60 * 60 * 1000;

/**
 * How often a rating kept for want of a connection is tried again by itself. Not left
 * to the live channel coming back alone: a signal lost for less than the socket takes
 * to notice is never a drop to it, and the rating would wait for the next one.
 */
export const RETRY_MS = 30_000;

let retry: ReturnType<typeof setTimeout> | null = null;

function retryLater(passengerId: string, deliver: RatingDelivery): void {
  if (retry !== null) return;
  retry = setTimeout(() => {
    retry = null;
    void flushRatings(passengerId, deliver);
  }, RETRY_MS);
}

function read(): HeldRating[] {
  try {
    const stored = localStorage.getItem(KEY);
    if (!stored) return [];
    const since = Date.now() - KEEP_FOR;
    return (JSON.parse(stored) as HeldRating[]).filter(
      (rating) => typeof rating?.rideId === 'string' && Date.parse(rating.at) > since,
    );
  } catch {
    return [];
  }
}

function write(ratings: HeldRating[]): void {
  try {
    if (ratings.length === 0) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify(ratings));
  } catch {
    // Held for as long as this tab is open, and no longer.
  }
}

/** What is waiting, oldest first. */
export function heldRatings(): HeldRating[] {
  return read();
}

function drop(rideId: string): void {
  write(read().filter((rating) => rating.rideId !== rideId));
}

async function send(rating: HeldRating, deliver: RatingDelivery): Promise<RatingLeft> {
  try {
    await deliver(rating);
  } catch (problem) {
    if (problem instanceof ApiError && problem.code === 'offline') {
      retryLater(rating.passengerId, deliver);
      return { kind: 'kept' };
    }
    drop(rating.rideId);
    // Rated already: from another device, or a first send whose answer was lost.
    if (problem instanceof ApiError && problem.code === 'already_rated') return { kind: 'sent' };
    return {
      kind: 'refused',
      message: problem instanceof ApiError ? problem.message : 'Что-то пошло не так',
    };
  }
  drop(rating.rideId);
  return { kind: 'sent' };
}

/** Keeps `rating` and tries to send it now. */
export function leaveRating(rating: HeldRating, deliver: RatingDelivery): Promise<RatingLeft> {
  write([...read().filter((r) => r.rideId !== rating.rideId), rating]);
  return send(rating, deliver);
}

let flushing = false;

/** Sends what `passengerId` has waiting, and stops at the first with no connection. */
export async function flushRatings(passengerId: string | null, deliver: RatingDelivery): Promise<void> {
  if (!passengerId || flushing) return;
  flushing = true;
  try {
    for (const rating of read()) {
      if (rating.passengerId !== passengerId) continue;
      if ((await send(rating, deliver)).kind === 'kept') break;
    }
  } finally {
    flushing = false;
  }
}

/** Lets go of everything: the passenger signed out. */
export function clearRatings(): void {
  if (retry !== null) clearTimeout(retry);
  retry = null;
  write([]);
}
